import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { GlobalConnectionDialogComponent, GlobalConnectionDialogData } from '../../shared/components/global-connection-dialog/global-connection-dialog.component';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';
import { Subscription } from 'rxjs';
import { VoiceCallService } from './voice-call.service';

@Injectable({
  providedIn: 'root'
})
export class IdleMonitorService implements OnDestroy {
  private readonly IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  
  private idleTimer: any = null;
  private isIdle = false;
  private dialogOpen = false;
  private networkDisconnected = false;
  private pendingIdleDialog = false;
  private pendingNetworkDialog = false;
  private callStateSub: Subscription = Subscription.EMPTY;
  
  // Activity events to monitor
  private readonly activityEvents = [
    'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'
  ];
  private readonly publicRoutePrefixes = [
    '/',
    '/login',
    '/login-service-provider',
    '/company/auth/login',
    '/otp-verification',
    '/country-selector',
    '/public/',
    '/contact-us',
    '/terms-conditions',
    '/privacy-policy',
  ];

  constructor(
    private dialog: MatDialog,
    private webSocketService: WebSocketService,
    private authService: AuthService,
    private voiceCallService: VoiceCallService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    //'🔍 [IDLE_MONITOR] Service constructor called');
    // There is no user to go idle and no DOM to listen on while prerendering.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.isPublicRoute()) {
      return;
    }
    // Skip for financial advisor sessions (no realtime requirement)
    const role = this.authService.getUserRole();
    if (role === 'financialadvisor') {
      return;
    }

    // Always start monitoring first so the idle timer is running regardless of
    // any later subscription set-up.
    this.startMonitoring();
    this.setupNetworkMonitoring();

    // After monitoring is running, subscribe to call state changes so that any
    // dialog that was deferred while a call was in progress is shown as soon as
    // the call ends (state returns to idle).
    try {
      this.callStateSub = this.voiceCallService.call$.subscribe((snap) => {
        if (snap.state !== 'idle' || this.dialogOpen) return;

        if (this.pendingNetworkDialog) {
          this.pendingNetworkDialog = false;
          this.handleNetworkDisconnection();
          return;
        }

        if (this.pendingIdleDialog) {
          this.pendingIdleDialog = false;
          this.handleIdleTimeout();
        }
      });
    } catch (err) {
      // Non-fatal: deferred-dialog-after-call feature unavailable, but core idle
      // monitoring continues unaffected.
      console.warn('[IDLE_MONITOR] Could not subscribe to voice call state', err);
    }
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.callStateSub.unsubscribe();
  }

  private isVoiceCallActive(): boolean {
    // Suppress reload dialogs only while an active call connection is established.
    try {
      return this.voiceCallService.snapshot.state === 'in_call';
    } catch {
      return false; // fail-safe: never suppress when state is unreadable
    }
  }

  /**
   * Setup network monitoring for immediate disconnection detection
   */
  private setupNetworkMonitoring(): void {
    // Listen for network status changes
    window.addEventListener('online', () => {
      //'🌐 [IDLE_MONITOR] Network back online');
      this.networkDisconnected = false;
    });

    window.addEventListener('offline', () => {
      ////'🌐 [IDLE_MONITOR] Network disconnected - showing dialog immediately');
      this.networkDisconnected = true;
      this.handleNetworkDisconnection();
    });

    // Also monitor WebSocket connection status
    this.webSocketService.connectionStatus$.subscribe(isConnected => {
      if (!isConnected && !this.networkDisconnected) {
        ////'🔌 [IDLE_MONITOR] WebSocket disconnected - showing dialog after short delay');
        // Wait 3 seconds to see if it reconnects
        setTimeout(() => {
          // Check current connection status
          this.webSocketService.connectionStatus$.subscribe(currentStatus => {
            if (!currentStatus && !this.dialogOpen) {
              this.handleNetworkDisconnection();
            }
          });
        }, 3000);
      }
    });
  }

  /**
   * Handle network disconnection - show dialog immediately
   */
  private handleNetworkDisconnection(): void {
    if (this.isPublicRoute()) {
      return;
    }

    if (this.isVoiceCallActive()) {
      this.pendingNetworkDialog = true;
      return;
    }

    // ✅ ADD: Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
    // console.log('🚨 [IDLE_MONITOR] User not authenticated, skipping connection dialog');
      return;
    }

    // Skip dialog for financial advisor
    if (this.authService.getUserRole() === 'financialadvisor') {
      return;
    }

    if (this.dialogOpen) {
      return; // Dialog already open
    }
    
    //console.log('🚨 [IDLE_MONITOR] Network disconnection detected - showing dialog immediately');
    
    this.isIdle = true;
    this.dialogOpen = true;
    this.pendingNetworkDialog = false;
    
    // Show the connection dialog immediately
    //console.log('🚨 [IDLE_MONITOR] Opening connection dialog...');
    const dialogRef = this.dialog.open(GlobalConnectionDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'ui.connection.messageLost',
        showRetry: false,
        showReload: true
      } as GlobalConnectionDialogData
    });
    
    console.log('🚨 [IDLE_MONITOR] Connection dialog opened successfully');

    dialogRef.afterClosed().subscribe(result => {
      console.log('🚨 [IDLE_MONITOR] Connection dialog closed with result:', result);
      this.dialogOpen = false;
      this.isIdle = false;
      
      if (result?.action === 'reload') {
        console.log('🔄 [IDLE_MONITOR] User chose to reload page');
        window.location.reload();
      }
    });
  }

  /**
   * Start monitoring user activity
   */
  private startMonitoring(): void {
    console.log('🔍 [IDLE_MONITOR] Starting idle monitoring (5-minute timeout)');
    
    this.resetIdleTimer();
    
    // Listen for user activity events
    this.activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.handleUserActivity();
      }, { passive: true });
    });
    
    console.log('✅ [IDLE_MONITOR] Idle monitoring started');
  }

  /**
   * Stop monitoring user activity
   */
  private stopMonitoring(): void {
    console.log('🔍 [IDLE_MONITOR] Stopping idle monitoring');

    // Nothing was ever started while prerendering, and there is no document to
    // detach listeners from.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    
    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, () => {
        this.handleUserActivity();
      });
    });
    
    console.log('✅ [IDLE_MONITOR] Idle monitoring stopped');
  }

  /**
   * Handle user activity - reset the idle timer
   */
  private handleUserActivity(): void {
    //'🔄 [IDLE_MONITOR] User activity detected, resetting timer');
    if (!this.isIdle) {
      this.resetIdleTimer();
    }
  }

  /**
   * Reset the idle timer
   */
  private resetIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    
    this.idleTimer = setTimeout(() => {
      this.handleIdleTimeout();
    }, this.IDLE_TIMEOUT);
    
    //'🔄 [IDLE_MONITOR] Idle timer reset, will trigger in', this.IDLE_TIMEOUT / 1000, 'seconds');
  }

  /**
   * Handle idle timeout - show dialog
   */
  private handleIdleTimeout(): void {
    if (this.isPublicRoute()) {
      return;
    }

    if (this.isVoiceCallActive()) {
      this.pendingIdleDialog = true;
      return;
    }

    // ✅ ADD: Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      //'🚨 [IDLE_MONITOR] User not authenticated, skipping idle dialog');
      return;
    }

    // Skip dialog for financial advisor
    if (this.authService.getUserRole() === 'financialadvisor') {
      return;
    }

    if (this.dialogOpen) {
      return; // Dialog already open
    }
    
    //'🚨 [IDLE_MONITOR] User is idle for 5 minutes - showing dialog');
    
    this.isIdle = true;
    this.dialogOpen = true;
    this.pendingIdleDialog = false;
    
    // Show the idle dialog
    //'🚨 [IDLE_MONITOR] Opening idle dialog...');
    const dialogRef = this.dialog.open(GlobalConnectionDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'ui.connection.messageGlobalInactivity',
        showRetry: false,
        showReload: true
      } as GlobalConnectionDialogData
    });
    
    //'🚨 [IDLE_MONITOR] Dialog opened successfully');

    dialogRef.afterClosed().subscribe(result => {
      this.dialogOpen = false;
      
      if (result?.action === 'reload') {
        //'🔄 [IDLE_MONITOR] User requested page reload');
        window.location.reload();
      } else {
        //'❌ [IDLE_MONITOR] User dismissed dialog - continuing monitoring');
        // Reset monitoring after dialog is dismissed
        this.isIdle = false;
        this.resetIdleTimer();
      }
    });
  }

  /**
   * Manually reset idle timer (can be called from other services)
   */
  public resetIdleTimerPublic(): void {
    this.isIdle = false;
    this.resetIdleTimer();
  }

  /**
   * Get current idle status
   */
  public isUserIdle(): boolean {
    return this.isIdle;
  }

  /**
   * Test method to manually trigger idle dialog (for testing)
   */
  public testIdleDialog(): void {
    //'🧪 [IDLE_MONITOR] Manually triggering idle dialog for testing');
    this.handleIdleTimeout();
  }

  private isPublicRoute(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const path = window.location.pathname || '/';
    return this.publicRoutePrefixes.some((prefix) => {
      if (prefix === '/') {
        return path === '/';
      }
      return path.startsWith(prefix);
    });
  }
}
